import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { DialogTitleWithClose } from '../../common';

/**
 * AITemplateGeneratorDialog Component
 *
 * Modal dialog for AI-powered template generation using the master template.
 * Allows users to describe their requirements and generate templates automatically.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Handler for closing the dialog
 * @param {string} props.aiPrompt - Current AI prompt input
 * @param {boolean} props.generatingTemplate - Whether template generation is in progress
 * @param {Function} props.setAiPrompt - Setter for AI prompt input
 * @param {Function} props.handleGenerateTemplate - Handler for generating template
 */
const AITemplateGeneratorDialog = ({
  open,
  onClose,
  aiPrompt,
  generatingTemplate,
  setAiPrompt,
  handleGenerateTemplate,
}) => {
  return (
    <Dialog
      open={open}
      onClose={generatingTemplate ? undefined : onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={generatingTemplate}
    >
      <DialogTitleWithClose
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
              🤖 AI Template Generator
            </Typography>
          </Box>
        }
        onClose={onClose}
        disableClose={generatingTemplate}
      />

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Let AI help you create a professional template based on your
            requirements using our built-in template generator.
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 3, display: 'block' }}
          >
            💡 Tip: Be specific about the template purpose, target audience, and
            expected output format for better results.
          </Typography>

          <TextField
            fullWidth
            label="Template Description"
            value={aiPrompt}
            onChange={e => setAiPrompt(e.target.value)}
            placeholder="Describe what kind of template you want to create... (e.g., 'Create a template for analyzing customer feedback that extracts sentiment, key issues, and action items')"
            multiline
            rows={5}
            disabled={generatingTemplate}
            sx={{ mb: 2 }}
            autoFocus
          />

          {generatingTemplate && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Generating your template... This may take a few moments.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={generatingTemplate}
          color="secondary"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateTemplate}
          disabled={generatingTemplate || !aiPrompt.trim()}
          startIcon={
            generatingTemplate ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <AutoAwesomeIcon />
            )
          }
        >
          {generatingTemplate ? 'Generating...' : 'Generate Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AITemplateGeneratorDialog;
